import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import {
  inject,
  InjectionToken,
  makeEnvironmentProviders,
  provideAppInitializer,
  type EnvironmentProviders,
} from '@angular/core';
import type { Observable } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { ApiError } from './api.error';
import { PaymentFailedError } from './payment-failed.error';

/**
 * HTTP seam for the Orval-generated client in `generated/client/`.
 *
 * Why a mutator at all, and why this shape
 * ----------------------------------------
 * Orval's `angular-query` client injects `HttpClient` inside each generated
 * `injectXxx()` hook — which *is* an injection context — and then threads that
 * instance down to the mutator as its second argument. So the generated call
 * looks like `enrollhereOrvalMutator({ url, method, ... }, http)`.
 *
 * That signature is the whole reason this library can share one client across
 * dialer, web and admin-web. Requests go out through the app's own
 * `HttpClient`, so every `HttpInterceptor` that app already registers — most
 * importantly its `authInterceptor` — runs unchanged. Common does not have to
 * know how any particular app authenticates, and nothing here is dialer
 * specific.
 *
 * Contrast the dialer's `enrollhere-orval-mutator.ts`, which this replaces: it
 * used a raw `fetch` mutator, so it had to reach for `environment.api.endpoint`
 * and a module-global Firebase `Auth` registered by an app initializer. Both are
 * app-local facts that a shared library must not hardcode.
 *
 * What still needs configuring, and how
 * -------------------------------------
 * Generated URLs are host-relative (`/v1/...`), so *something* has to supply the
 * API origin. `provideEnrollhereApiClient()` takes it — plus an optional token
 * getter — through DI, and an app initializer hands the resolved value to this
 * module. The mutator itself runs inside a TanStack `queryFn`, long after the
 * injection context is gone, which is why the config is read from a holder
 * rather than injected at call time.
 *
 * Configuring is optional. With no `baseUrl` the URL is left relative, which is
 * exactly right for an app whose interceptor already rewrites the origin.
 * Likewise `getAuthToken` is only needed by an app that does *not* already
 * attach a bearer token in an interceptor.
 *
 * Errors are mapped to the same contract `EnrollhereApiService` raises —
 * {@link PaymentFailedError} on 402, {@link ApiError} otherwise — so callers
 * handle generated and hand-written calls identically.
 */

/** Request descriptor Orval builds for every generated operation. */
export interface OrvalRequestConfig {
  url: string;
  method: string;
  headers?: Record<string, string>;
  params?: Record<string, unknown>;
  data?: unknown;
  signal?: AbortSignal;
}

export interface EnrollhereApiClientConfig {
  /**
   * API origin, e.g. `https://api.enrollhere.com`. No trailing slash and no
   * `/v1` — generated paths already carry their own `/v1` prefix, so adding it
   * here would produce `/v1/v1/...`. Leave unset to keep URLs relative.
   */
  baseUrl?: string;

  /**
   * Returns the bearer token for the next request, or `null` to send none.
   * Called per request, so a refreshed token is picked up without re-providing.
   * Omit it when the app's own interceptor already sets `Authorization`.
   */
  getAuthToken?: () => Promise<string | null> | string | null;

  /** Per-request timeout. Defaults to 30s; set `0` to disable. */
  timeoutMs?: number;
}

export const ENROLLHERE_API_CLIENT_CONFIG =
  new InjectionToken<EnrollhereApiClientConfig>('ENROLLHERE_API_CLIENT_CONFIG');

const DEFAULT_TIMEOUT_MS = 30_000;

let clientConfig: EnrollhereApiClientConfig = {};

/**
 * Escape hatch for tests and for apps that bootstrap outside Angular DI.
 * Prefer {@link provideEnrollhereApiClient}.
 */
export function setEnrollhereApiClientConfig(
  config: EnrollhereApiClientConfig
): void {
  clientConfig = config;
}

/** Current configuration. Exposed for assertions in tests. */
export function getEnrollhereApiClientConfig(): EnrollhereApiClientConfig {
  return clientConfig;
}

/**
 * Configures the generated client for one application.
 *
 * The factory runs in an injection context, so it may `inject()` whatever the
 * app keeps its endpoint and credentials in:
 *
 * ```ts
 * provideEnrollhereApiClient(() => {
 *   const auth = inject(AUTH);
 *   return {
 *     baseUrl: environment.api.endpoint,
 *     getAuthToken: () =>
 *       auth.currentUser ? getIdToken(auth.currentUser) : null,
 *   };
 * });
 * ```
 */
export function provideEnrollhereApiClient(
  configFactory: () => EnrollhereApiClientConfig
): EnvironmentProviders {
  return makeEnvironmentProviders([
    { provide: ENROLLHERE_API_CLIENT_CONFIG, useFactory: configFactory },
    provideAppInitializer(() => {
      setEnrollhereApiClientConfig(inject(ENROLLHERE_API_CLIENT_CONFIG));
    }),
  ]);
}

function resolveUrl(url: string): string {
  if (/^https?:\/\//i.test(url)) {
    return url;
  }
  const base = (clientConfig.baseUrl ?? '').replace(/\/+$/, '');
  if (!base) {
    return url;
  }
  return `${base}${url.startsWith('/') ? url : `/${url}`}`;
}

/**
 * Angular refuses non-string param values, and drops `null`/`undefined` rather
 * than sending the string "undefined". Arrays repeat the key, matching how
 * Fastify parses querystring arrays.
 */
function toHttpParams(
  params: Record<string, unknown> | undefined
): HttpParams | undefined {
  if (!params) {
    return undefined;
  }
  let httpParams = new HttpParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) {
      continue;
    }
    const values = Array.isArray(value) ? value : [value];
    for (const item of values) {
      if (item === undefined || item === null) {
        continue;
      }
      httpParams = httpParams.append(
        key,
        typeof item === 'object' ? JSON.stringify(item) : String(item)
      );
    }
  }
  return httpParams;
}

/**
 * `HttpErrorResponse.error` is already-parsed JSON for a JSON body, but a raw
 * string when parsing failed. `ApiError` stores the response as text and parses
 * lazily in `serverMessage`, so re-serialize objects back to JSON.
 */
function errorBodyToText(body: unknown): string {
  if (body === undefined || body === null) {
    return '';
  }
  if (typeof body === 'string') {
    return body;
  }
  try {
    return JSON.stringify(body);
  } catch {
    return '';
  }
}

function toApiError(error: unknown): unknown {
  if (!(error instanceof HttpErrorResponse)) {
    return error;
  }
  if (error.status === 402) {
    return new PaymentFailedError();
  }
  return new ApiError(
    error.status,
    error.statusText,
    errorBodyToText(error.error)
  );
}

function abortError(): Error {
  return new DOMException('The operation was aborted.', 'AbortError');
}

/**
 * Awaits the response, honouring TanStack Query's `AbortSignal`.
 *
 * Unsubscribing is what actually cancels the in-flight request, so a superseded
 * query stops occupying a connection instead of merely having its result
 * discarded — something the dialer's `fetch` mutator could only approximate.
 */
function firstValue<T>(
  source$: Observable<T>,
  signal: AbortSignal | undefined,
  timeoutMs: number
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }

    let settled = false;
    const onAbort = (): void => {
      settle(() => {
        subscription.unsubscribe();
        reject(abortError());
      });
    };

    const settle = (complete: () => void): void => {
      if (settled) {
        return;
      }
      settled = true;
      signal?.removeEventListener('abort', onAbort);
      complete();
    };

    const source = timeoutMs > 0 ? source$.pipe(timeout(timeoutMs)) : source$;
    const subscription = source.subscribe({
      next: value => settle(() => resolve(value)),
      error: (error: unknown) => settle(() => reject(toApiError(error))),
      // A 204 completes without emitting; generated types model that as `void`.
      complete: () => settle(() => resolve(undefined as T)),
    });

    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Orval mutator. Referenced by `orval.config.ts`; not called by hand.
 */
export async function enrollhereOrvalMutator<T>(
  config: OrvalRequestConfig,
  http?: HttpClient
): Promise<T> {
  if (!http) {
    throw new Error('enrollhereOrvalMutator requires an HttpClient');
  }
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...config.headers,
  };

  const token = await clientConfig.getAuthToken?.();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const request$ = http.request<T>(config.method, resolveUrl(config.url), {
    body: config.data,
    headers,
    params: toHttpParams(config.params),
    observe: 'body',
    responseType: 'json',
  });

  return firstValue(
    request$,
    config.signal,
    clientConfig.timeoutMs ?? DEFAULT_TIMEOUT_MS
  );
}
